import { memo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button, Chip } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../constants';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { CalendarNavigationConfig } from '../types';

const TopCornerCellsComponent = ({
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  timezone,
}: CalendarNavigationConfig & { timezone?: string }) => {
  return (
    <>
      <div data-top-left-corner css={[topLeftCornerStyle, timezone && topLeftCornerWithTimezoneStyle]}>
        <Button
          variant='secondary'
          iconName='caret-left'
          disabled={!canGoPrevious}
          onClick={onPrevious}
          css={{ boxShadow: theme.shadows.light, backgroundColor: theme.colors.neutral5 }}
        />
        {!!timezone && (
          <div css={timezoneContainerStyle}>
            <Chip.TimeZone>{timezone}</Chip.TimeZone>
          </div>
        )}
      </div>
      <div data-top-right-corner css={topRightCornerStyle}>
        <Button variant='secondary' iconName='caret-right' disabled={!canGoNext} onClick={onNext} />
      </div>
    </>
  );
};

const TopCornerCellsForScroll = ({ onScroll }: { onScroll: (direction: 'left' | 'right') => void }) => {
  const { canScrollLeft, canScrollRight } = useCalendarGridStore(['canScrollLeft', 'canScrollRight']);
  const baseTimezone = useCalendarGridStore((state) => state.groupsById[state.groupIds[0]]?.timezone);

  return (
    <TopCornerCellsComponent
      onNext={() => onScroll('right')}
      onPrevious={() => onScroll('left')}
      canGoNext={canScrollRight}
      canGoPrevious={canScrollLeft}
      timezone={baseTimezone}
    />
  );
};

interface TopCornerCellsProps {
  onScroll: (direction: 'left' | 'right') => void;
  navigationConfig?: CalendarNavigationConfig;
}

export const TopCornerCells = memo(({ onScroll, navigationConfig }: TopCornerCellsProps) => {
  if (navigationConfig) return <TopCornerCellsComponent {...navigationConfig} />;

  return <TopCornerCellsForScroll onScroll={onScroll} />;
});
TopCornerCells.displayName = 'TopCornerCells';

const topRightCornerStyle = css({
  gridColumn: '-2 / -1',
  gridRow: 1,
  backgroundColor: theme.colors.neutral5,
  boxShadow: theme.shadows.light,
  zIndex: CALENDAR_Z_INDICES.topCornerCell,
  position: 'sticky',
  top: 0,
  right: 0,
  button: {
    maxWidth: 'var(--group-separator-width)',
    borderRadius: 0,
    height: '100%',
    width: '100%',
  },
});

const topLeftCornerStyle = css({
  zIndex: CALENDAR_Z_INDICES.topCornerCell,
  position: 'sticky',
  top: 0,
  left: 0,
  gridColumn: '1 / 1',
  gridRow: '1 / 2',

  boxShadow: 'none',
  backgroundColor: 'unset',
  button: {
    maxWidth: 'var(--group-separator-width)',
    borderRadius: 0,
    height: '100%',
    width: '100%',
  },
});

const topLeftCornerWithTimezoneStyle = css({
  gridRow: '1 / 3',
  display: 'grid',
  gridTemplateRows: '1fr var(--grid-body-spacer-size)',
  gridTemplateColumns: '1fr',
});

const timezoneContainerStyle = css({
  userSelect: 'none',
  backdropFilter: 'blur(4px)',
  borderBottom: `1px solid ${theme.colors.neutral5}`,
  borderTop: `1px solid ${theme.colors.neutral10}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 0 4px ${theme.colors.neutral20}`,
  maxWidth: 'var(--time-column-width)',
});
